<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative max-w-7xl mx-auto space-y-6">
      <!-- Header -->
      <div class="card">
        <div class="px-6 py-4 border-b border-gray-800 flex justify-between items-center">
          <div class="flex items-center space-x-4">
            <div class="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-red rounded-xl flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-semibold text-white font-display">Debug Console</h1>
              <p class="text-sm text-gray-400">Тестирование событий в реальном времени</p>
            </div>
          </div>

          <!-- Connection Status -->
          <div class="flex items-center space-x-3">
            <div
              class="flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium"
              :class="connectionStatusClass"
            >
              <span class="relative flex h-2.5 w-2.5">
                <span
                  v-if="pushx.isConnected.value"
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex rounded-full h-2.5 w-2.5"
                  :class="connectionDotClass"
                ></span>
              </span>
              <span>{{ connectionStatusText }}</span>
            </div>
            <span v-if="pushx.socketId.value" class="text-xs text-gray-500 font-mono">
              ID: {{ pushx.socketId.value }}
            </span>
          </div>
        </div>

        <!-- App Selection -->
        <div class="px-6 py-4 bg-gray-900/30">
          <div class="flex flex-wrap items-center gap-4">
            <div class="flex-1 min-w-[250px]">
              <label class="form-label">Приложение</label>
              <select
                v-model="selectedAppId"
                class="input-field"
                @change="onAppChange"
              >
                <option value="">Выберите приложение</option>
                <option
                  v-for="app in applications"
                  :key="app.id"
                  :value="app.id"
                >
                  {{ app.name }} ({{ app.key }})
                </option>
              </select>
            </div>
            <div>
              <label class="form-label">&nbsp;</label>
              <button
                v-if="!pushx.isConnected.value"
                @click="connect"
                :disabled="!selectedApp"
                class="btn-primary flex items-center h-[42px]"
                :class="{ 'opacity-50 cursor-not-allowed': !selectedApp }"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
                Подключиться
              </button>
              <button
                v-else
                @click="disconnect"
                class="btn-danger flex items-center h-[42px]"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                </svg>
                Отключиться
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:min-h-[420px]">
        <!-- Send Event Panel -->
        <div class="card flex flex-col min-h-0">
          <div class="px-6 py-4 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center space-x-2">
              <svg class="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12"></path>
              </svg>
              <h2 class="text-lg font-semibold text-white font-display">Отправить событие</h2>
            </div>
          </div>

          <div class="p-6 flex-1 flex flex-col gap-4 min-h-0">
            <!-- Channel Input -->
            <div>
              <label class="form-label">Канал</label>
              <div class="flex gap-2">
                <select v-model="channelType" class="input-field w-40">
                  <option 
                    v-for="opt in channelTypeOptions" 
                    :key="opt.value" 
                    :value="opt.value"
                    :disabled="!opt.available"
                  >
                    {{ opt.label }}{{ !opt.available ? ' 🔒' : '' }}
                  </option>
                </select>
                <input
                  v-model="channelName"
                  type="text"
                  class="input-field flex-1"
                  placeholder="название-канала"
                />
              </div>
              <p class="mt-1 text-xs text-gray-500">
                Полное имя: <span class="font-mono text-gray-400">{{ fullChannelName }}</span>
              </p>
              <!-- Warning for locked channel type -->
              <p v-if="!isChannelTypeAllowed" class="mt-2 text-xs text-amber-400 flex items-center">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
                {{ channelType === 'private' ? 'Приватные каналы требуют тариф Starter+' : 'Presence-каналы требуют тариф Pro+' }}
              </p>
            </div>

            <!-- Event Name -->
            <div>
              <label class="form-label">Событие</label>
              <input
                v-model="eventName"
                type="text"
                class="input-field"
                placeholder="message"
              />
            </div>

            <!-- JSON Data -->
            <div class="flex-1 flex flex-col min-h-0">
              <label class="form-label flex-shrink-0">Данные (JSON)</label>
              <div class="relative flex-1 min-h-[120px] min-w-0">
                <textarea
                  v-model="eventData"
                  class="input-field w-full h-full min-h-[120px] font-mono text-sm resize-none absolute inset-0"
                  :class="{ 'border-red-500': jsonError }"
                  placeholder='{"message": "Hello, World!"}'
                  @input="validateJson"
                ></textarea>
                <div
                  v-if="jsonError"
                  class="absolute bottom-2 left-2 right-2 bg-red-900/90 text-red-300 text-xs px-2 py-1 rounded"
                >
                  {{ jsonError }}
                </div>
              </div>
            </div>

            <!-- Send Button -->
            <button
              @click="sendEvent"
              :disabled="!canSendEvent"
              class="btn-primary w-full flex items-center justify-center"
              :class="{ 'opacity-50 cursor-not-allowed': !canSendEvent }"
              :title="sendButtonTooltip"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
              </svg>
              Отправить
            </button>
            <!-- No subscription warning under button -->
            <p v-if="pushx.isConnected.value && !hasActiveSubscription && billingLoaded" class="text-xs text-amber-400 text-center mt-2">
              <router-link to="/billing" class="hover:underline">Активируйте подписку для отправки сообщений →</router-link>
            </p>
          </div>
        </div>

        <!-- Subscriptions Panel -->
        <div class="card flex flex-col">
          <div class="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <svg class="w-5 h-5 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
              </svg>
              <h2 class="text-lg font-semibold text-white font-display">Подписки</h2>
            </div>
          </div>

          <div class="p-6 space-y-4 flex-1 flex flex-col">
            <!-- Subscribe Form -->
            <div class="space-y-2">
              <div class="flex gap-2">
                <select v-model="subscribeChannelType" class="input-field w-40">
                  <option 
                    v-for="opt in channelTypeOptions" 
                    :key="opt.value" 
                    :value="opt.value"
                    :disabled="!opt.available"
                  >
                    {{ opt.label }}{{ !opt.available ? ' 🔒' : '' }}
                  </option>
                </select>
                <input
                  v-model="subscribeChannelName"
                  type="text"
                  class="input-field flex-1"
                  placeholder="название-канала"
                  @keyup.enter="subscribeToChannel"
                />
                <button
                  @click="subscribeToChannel"
                  :disabled="!canSubscribe"
                  class="btn-secondary px-4"
                  :class="{ 'opacity-50 cursor-not-allowed': !canSubscribe }"
                  :title="!isSubscribeChannelTypeAllowed ? 'Этот тип канала недоступен на вашем тарифе' : ''"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </button>
              </div>
              <!-- Warning for locked channel type -->
              <p v-if="!isSubscribeChannelTypeAllowed && subscribeChannelType !== 'public'" class="text-xs text-amber-400 flex items-center">
                <svg class="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
                {{ subscribeChannelType === 'private' ? 'Приватные каналы — тариф Starter+' : 'Presence-каналы — тариф Pro+' }}
              </p>
              <!-- Hint: subscribe to avoid disconnect -->
              <p
                v-if="pushx.isConnected.value && subscribedChannels.length === 0"
                class="text-xs text-amber-400/90 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2"
              >
                <svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>Подпишитесь на канал в течение ~10 секунд после подключения — иначе сервер разорвёт соединение (таймаут из‑за отсутствия подписок).</span>
              </p>
            </div>

            <!-- Subscribed Channels List -->
            <div class="flex-1 overflow-y-auto space-y-2 min-h-[150px]">
              <div
                v-if="subscribedChannels.length === 0"
                class="h-full flex items-center justify-center text-gray-500 text-sm"
              >
                <div class="text-center px-2">
                  <svg class="w-12 h-12 mx-auto mb-2 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"></path>
                  </svg>
                  <p>Нет активных подписок</p>
                  <p v-if="pushx.isConnected.value" class="text-amber-400/90 text-xs mt-1 max-w-[220px] mx-auto">Подпишитесь на канал выше, иначе через ~10 сек соединение будет разорвано.</p>
                </div>
              </div>
              <div
                v-for="channel in subscribedChannels"
                :key="channel.name"
                class="flex items-center justify-between px-3 py-2 bg-gray-800/50 rounded-lg group hover:bg-gray-800 transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <span
                    class="w-2 h-2 rounded-full"
                    :class="channel.subscribed ? 'bg-green-500' : 'bg-yellow-500'"
                  ></span>
                  <span class="font-mono text-sm text-gray-300">{{ channel.name }}</span>
                  <span
                    class="badge text-xs"
                    :class="getChannelTypeBadgeClass(channel.type)"
                  >
                    {{ channel.type }}
                  </span>
                </div>
                <button
                  @click="unsubscribeFromChannel(channel.name)"
                  class="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-all"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Events Log -->
      <div class="card">
        <div class="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16l-4-4m0 0l4-4m-4 4h18"></path>
            </svg>
            <h2 class="text-lg font-semibold text-white font-display">Полученные события</h2>
            <span class="badge badge-info">{{ events.length }}</span>
          </div>
          <div class="flex items-center space-x-2">
            <!-- Filter -->
            <input
              v-model="eventFilter"
              type="text"
              class="input-field w-48 text-sm py-1.5"
              placeholder="Фильтр..."
            />
            <button
              @click="clearEvents"
              class="btn-secondary text-sm py-1.5 flex items-center"
            >
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
              Очистить
            </button>
            <button
              @click="exportEvents"
              class="btn-secondary text-sm py-1.5 flex items-center"
            >
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
              </svg>
              Экспорт
            </button>
          </div>
        </div>

        <div class="events-container overflow-y-auto max-h-[400px]">
          <div
            v-if="filteredEvents.length === 0"
            class="py-12 text-center text-gray-500"
          >
            <svg class="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
            </svg>
            <p class="text-lg mb-2">Нет событий</p>
            <p class="text-sm text-gray-600">Подпишитесь на канал и ожидайте входящих событий</p>
          </div>

          <div
            v-for="(event, index) in filteredEvents"
            :key="index"
            class="px-6 py-4 border-b border-gray-800 hover:bg-gray-800/30 transition-colors"
          >
            <div class="flex items-start justify-between mb-2">
              <div class="flex items-center space-x-3">
                <span
                  class="w-2 h-2 rounded-full"
                  :class="getEventTypeColor(event.type)"
                ></span>
                <span class="text-sm text-gray-400">{{ event.timestamp }}</span>
                <span class="font-mono text-sm text-primary-500">{{ event.channel }}</span>
                <span class="badge badge-info">{{ event.event }}</span>
              </div>
              <span
                class="badge text-xs"
                :class="event.type === 'sent' ? 'badge-success' : 'badge-warning'"
              >
                {{ event.type === 'sent' ? 'отправлено' : 'получено' }}
              </span>
            </div>
            <div class="ml-5">
              <pre class="text-sm text-gray-300 bg-gray-800/50 p-3 rounded-lg overflow-x-auto font-mono">{{ formatJson(event.data) }}</pre>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Tips -->
      <div class="card">
        <div class="px-6 py-4 border-b border-gray-800">
          <h2 class="text-lg font-semibold text-white font-display flex items-center">
            <svg class="w-5 h-5 mr-2 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Типы каналов
          </h2>
        </div>
        <div class="px-6 py-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <!-- Public -->
            <div class="flex items-start space-x-2 text-gray-400">
              <span class="text-green-500">✓</span>
              <div>
                <strong class="text-gray-300">public</strong>
                <span class="text-gray-500"> — публичные каналы, не требуют авторизации</span>
              </div>
            </div>
            <!-- Private -->
            <div class="flex items-start space-x-2" :class="canUsePrivateChannels ? 'text-gray-400' : 'text-gray-600'">
              <span :class="canUsePrivateChannels ? 'text-green-500' : 'text-gray-600'">
                {{ canUsePrivateChannels ? '✓' : '🔒' }}
              </span>
              <div>
                <strong :class="canUsePrivateChannels ? 'text-gray-300' : 'text-gray-500'">private-</strong>
                <span class="text-gray-500"> — приватные каналы</span>
                <span v-if="!canUsePrivateChannels" class="text-amber-500/70 text-xs block">Starter+</span>
              </div>
            </div>
            <!-- Presence -->
            <div class="flex items-start space-x-2" :class="canUsePresenceChannels ? 'text-gray-400' : 'text-gray-600'">
              <span :class="canUsePresenceChannels ? 'text-green-500' : 'text-gray-600'">
                {{ canUsePresenceChannels ? '✓' : '🔒' }}
              </span>
              <div>
                <strong :class="canUsePresenceChannels ? 'text-gray-300' : 'text-gray-500'">presence-</strong>
                <span class="text-gray-500"> — каналы с информацией о присутствии</span>
                <span v-if="!canUsePresenceChannels" class="text-amber-500/70 text-xs block">Pro+</span>
              </div>
            </div>
          </div>
          
          <!-- Subscription warning -->
          <div v-if="billingLoaded && !hasActiveSubscription" class="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start space-x-3">
            <svg class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <div class="text-sm">
              <p class="text-amber-400 font-medium">Требуется подписка</p>
              <p class="text-gray-400 mt-1">
                Для отправки сообщений необходима активная подписка. 
                <router-link to="/billing" class="text-primary-400 hover:underline">Перейти в раздел Биллинг →</router-link>
              </p>
            </div>
          </div>

          <!-- Vue SDK Info -->
          <div class="mt-4 p-3 bg-primary-500/10 border border-primary-500/30 rounded-lg">
            <div class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div class="text-sm">
                <p class="text-primary-400 font-medium">Vue SDK</p>
                <p class="text-gray-400 mt-1">
                  Эта консоль использует <code class="text-primary-300 bg-primary-500/20 px-1 rounded">usePushX</code> composable. 
                  Смотрите <a href="https://pushx.pro/docs#vue-sdk" target="_blank" class="text-primary-400 hover:underline">документацию</a> 
                  или <a href="https://www.npmjs.com/package/@pushx/vue" target="_blank" class="text-primary-400 hover:underline">npm-пакет</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useApplicationsStore } from '../stores/applications';
import { useBillingStore } from '../stores/billing';
import { useNotifications } from '../composables/useNotifications';
import { usePushX, ConnectionStates, ChannelTypes } from '../composables/usePushX';
import api from '../services/api';

// Stores
const applicationsStore = useApplicationsStore();
const billingStore = useBillingStore();
const { success, error: showError, warning } = useNotifications();

// Инициализация PushX Vue SDK
const pushx = usePushX();

// App selection
const selectedAppId = ref('');
const billingLoaded = ref(false);

// Send event form
const channelType = ref('public');
const channelName = ref('test');
const eventName = ref('message');
const eventData = ref('{\n  "text": "Hello, World!"\n}');
const jsonError = ref(null);

// Subscriptions
const subscribeChannelType = ref('public');
const subscribeChannelName = ref('');
const subscribedChannels = ref([]);

// Events log
const events = ref([]);
const eventFilter = ref('');

// Computed
const applications = computed(() => applicationsStore.applicationsList);
const selectedApp = computed(() => applications.value.find(app => app.id === selectedAppId.value));

const fullChannelName = computed(() => {
  if (channelType.value === 'public') return channelName.value;
  return `${channelType.value}-${channelName.value}`;
});

const fullSubscribeChannelName = computed(() => {
  if (subscribeChannelType.value === 'public') return subscribeChannelName.value;
  return `${subscribeChannelType.value}-${subscribeChannelName.value}`;
});

// Billing computed
const hasActiveSubscription = computed(() => billingStore.hasSubscription);
const canUsePrivateChannels = computed(() => billingStore.supportsPrivateChannels);
const canUsePresenceChannels = computed(() => billingStore.supportsPresenceChannels);

const isChannelTypeAllowed = computed(() => {
  if (channelType.value === 'public') return true;
  if (channelType.value === 'private') return canUsePrivateChannels.value;
  if (channelType.value === 'presence') return canUsePresenceChannels.value;
  return false;
});

const isSubscribeChannelTypeAllowed = computed(() => {
  if (subscribeChannelType.value === 'public') return true;
  if (subscribeChannelType.value === 'private') return canUsePrivateChannels.value;
  if (subscribeChannelType.value === 'presence') return canUsePresenceChannels.value;
  return false;
});

const channelTypeOptions = computed(() => [
  { value: 'public', label: 'public', available: true },
  { value: 'private', label: 'private-', available: canUsePrivateChannels.value },
  { value: 'presence', label: 'presence-', available: canUsePresenceChannels.value },
]);

const canSendEvent = computed(() => {
  return pushx.isConnected.value && 
         channelName.value && 
         eventName.value && 
         !jsonError.value && 
         hasActiveSubscription.value &&
         isChannelTypeAllowed.value;
});

const sendButtonTooltip = computed(() => {
  if (!pushx.isConnected.value) return 'Сначала подключитесь к WebSocket';
  if (!channelName.value) return 'Укажите название канала';
  if (!eventName.value) return 'Укажите название события';
  if (jsonError.value) return 'Исправьте ошибки в JSON';
  if (!hasActiveSubscription.value) return 'Требуется активная подписка';
  if (!isChannelTypeAllowed.value) {
    return channelType.value === 'private' 
      ? 'Приватные каналы требуют тариф Starter+' 
      : 'Presence-каналы требуют тариф Pro+';
  }
  return '';
});

const canSubscribe = computed(() => {
  return pushx.isConnected.value && subscribeChannelName.value && isSubscribeChannelTypeAllowed.value;
});

const filteredEvents = computed(() => {
  if (!eventFilter.value) return events.value;
  const filter = eventFilter.value.toLowerCase();
  return events.value.filter(event => 
    event.channel.toLowerCase().includes(filter) ||
    event.event.toLowerCase().includes(filter) ||
    JSON.stringify(event.data).toLowerCase().includes(filter)
  );
});

const connectionStatusClass = computed(() => {
  switch (pushx.connectionState.value) {
    case ConnectionStates.CONNECTED:
      return 'bg-green-500/20 text-green-400 border border-green-500/30';
    case ConnectionStates.CONNECTING:
    case ConnectionStates.RECONNECTING:
      return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
    default:
      return 'bg-gray-700/50 text-gray-400 border border-gray-600';
  }
});

const connectionDotClass = computed(() => {
  switch (pushx.connectionState.value) {
    case ConnectionStates.CONNECTED:
      return 'bg-green-500';
    case ConnectionStates.CONNECTING:
    case ConnectionStates.RECONNECTING:
      return 'bg-yellow-500';
    default:
      return 'bg-gray-500';
  }
});

const connectionStatusText = computed(() => {
  switch (pushx.connectionState.value) {
    case ConnectionStates.CONNECTED:
      return 'Подключено';
    case ConnectionStates.CONNECTING:
      return 'Подключение...';
    case ConnectionStates.RECONNECTING:
      return `Переподключение (${pushx.reconnectAttempts.value})...`;
    case ConnectionStates.FAILED:
      return 'Ошибка подключения';
    default:
      return 'Отключено';
  }
});

// Methods
async function fetchApplications() {
  await applicationsStore.fetchApplications();
  if (applications.value.length > 0 && !selectedAppId.value) {
    selectedAppId.value = applications.value[0].id;
  }
}

async function fetchBillingInfo() {
  try {
    await billingStore.fetchSubscription();
    billingLoaded.value = true;
  } catch (err) {
    console.warn('Failed to fetch billing info:', err);
    billingLoaded.value = true;
  }
}

function onAppChange() {
  if (pushx.isConnected.value) {
    disconnect();
  }
}

function connect() {
  if (!selectedApp.value) {
    showError('Ошибка', 'Выберите приложение');
    return;
  }

  const baseWsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8081/app';
  const cleanBaseUrl = baseWsUrl.replace(/\/$/, '');

  // Подключаемся через Vue SDK
  pushx.connect({
    appKey: selectedApp.value.key,
    wsUrl: cleanBaseUrl,
  });
}

/**
 * Генерация HMAC-SHA256 подписи (Web Crypto API)
 * Используется только в Debug Console, где разработчик имеет доступ к секретному ключу
 */
async function generateHmacSignature(message, secret) {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secret);
  const messageData = encoder.encode(message);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);

  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function disconnect() {
  pushx.disconnect();
  subscribedChannels.value = [];
}

async function subscribeToChannel() {
  if (!canSubscribe.value) return;

  const channelFullName = fullSubscribeChannelName.value;

  // Проверяем дубликаты
  if (subscribedChannels.value.find(c => c.name === channelFullName)) {
    showError('Ошибка', 'Уже подписаны на этот канал');
    return;
  }

  // Добавляем в локальный список сразу (пока ждём подписку)
  const channelInfo = {
    name: channelFullName,
    fullName: pushx.formatChannelName(channelFullName),
    type: subscribeChannelType.value,
    subscribed: false,
  };
  subscribedChannels.value.push(channelInfo);

  // Для приватных/presence каналов генерируем подпись локально
  let subscribeOptions = {};
  const isPrivate = channelFullName.startsWith('private-') || channelFullName.startsWith('presence-');
  
  if (isPrivate && selectedApp.value) {
    // Ждём socketId
    if (!pushx.socketId.value) {
      showError('Ошибка', 'Socket ID не доступен. Подождите подключения.');
      subscribedChannels.value = subscribedChannels.value.filter(c => c.name !== channelFullName);
      return;
    }

    try {
      const fullName = pushx.formatChannelName(channelFullName);
      const signatureString = `${pushx.socketId.value}:${fullName}`;
      const secret = await applicationsStore.fetchSecret(selectedApp.value.id);
      subscribeOptions.signature = await generateHmacSignature(signatureString, secret);
    } catch (err) {
      console.error('Failed to generate signature:', err);
      showError('Ошибка', 'Не удалось сгенерировать подпись');
      subscribedChannels.value = subscribedChannels.value.filter(c => c.name !== channelFullName);
      return;
    }
  }

  // Подписываемся через Vue SDK
  const channel = pushx.subscribe(channelFullName, subscribeOptions);
  
  // Обновляем fullName
  channelInfo.fullName = channel.name;

  // Слушаем события канала
  channel.on('subscribed', () => {
    const ch = subscribedChannels.value.find(c => c.name === channelFullName);
    if (ch) ch.subscribed = true;
  });

  subscribeChannelName.value = '';
}

function unsubscribeFromChannel(channelFullName) {
  pushx.unsubscribe(channelFullName);
  subscribedChannels.value = subscribedChannels.value.filter(c => c.name !== channelFullName);
}

async function sendEvent() {
  if (!canSendEvent.value) return;

  if (!isChannelTypeAllowed.value) {
    const channelTypeName = channelType.value === 'private' ? 'Приватные каналы' : 'Presence-каналы';
    const requiredPlan = channelType.value === 'private' ? 'Starter' : 'Pro';
    showError('Недоступно на вашем тарифе', `${channelTypeName} требуют тариф ${requiredPlan} или выше`);
    return;
  }

  let data;
  try {
    data = JSON.parse(eventData.value);
  } catch (err) {
    jsonError.value = 'Невалидный JSON';
    return;
  }

  const channelWithKey = pushx.formatChannelName(fullChannelName.value);

  try {
    const requestBody = JSON.stringify({
      channel: channelWithKey,
      event: eventName.value,
      data: data,
    });

    const timestamp = Math.floor(Date.now() / 1000).toString();
    const signatureString = requestBody + timestamp;
    const secret = await applicationsStore.fetchSecret(selectedApp.value.id);
    const signature = await generateHmacSignature(signatureString, secret);

    await api.post('/messages/send', requestBody, {
      headers: {
        'Content-Type': 'application/json',
        'X-PushX-Key': selectedApp.value.key,
        'X-PushX-Signature': signature,
        'X-PushX-Timestamp': timestamp,
      },
    });

    addEvent({
      type: 'sent',
      channel: fullChannelName.value,
      event: eventName.value,
      data: data,
      timestamp: formatTime(new Date()),
    });

    success('Отправлено', `Событие ${eventName.value} отправлено в канал ${fullChannelName.value}`);
  } catch (err) {
    console.error('Send error:', err);
    handleSendError(err);
  }
}

function handleSendError(err) {
  const status = err.response?.status;
  const errorCode = err.response?.data?.error;
  const message = err.response?.data?.message;

  if (status === 402) {
    if (errorCode === 'subscription_required') {
      showError('Требуется подписка', 'Для отправки сообщений необходима активная подписка.');
    } else {
      showError('Требуется оплата', message || 'Проверьте статус вашей подписки');
    }
    return;
  }

  if (status === 403) {
    if (errorCode === 'feature_not_available') {
      showError('Функция недоступна', message || 'Этот тип канала недоступен на вашем тарифе.');
    } else {
      showError('Доступ запрещён', message || 'У вас нет доступа к этой операции');
    }
    return;
  }

  if (status === 429) {
    if (errorCode === 'limit_exceeded') {
      showError('Лимит исчерпан', message || 'Превышен лимит сообщений.');
    } else {
      showError('Слишком много запросов', 'Подождите немного и попробуйте снова');
    }
    return;
  }

  if (status === 401) {
    showError('Ошибка авторизации', message || 'Проверьте ключи приложения');
    return;
  }

  showError('Ошибка отправки', message || 'Не удалось отправить событие');
}

function addEvent(event) {
  events.value.unshift(event);
  if (events.value.length > 100) {
    events.value = events.value.slice(0, 100);
  }
}

function clearEvents() {
  events.value = [];
}

function exportEvents() {
  const data = JSON.stringify(events.value, null, 2);
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pushx-events-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function validateJson() {
  if (!eventData.value.trim()) {
    jsonError.value = null;
    return;
  }
  try {
    JSON.parse(eventData.value);
    jsonError.value = null;
  } catch (err) {
    jsonError.value = err.message;
  }
}

function formatJson(data) {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function formatTime(date) {
  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getChannelTypeBadgeClass(type) {
  switch (type) {
    case 'private':
      return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
    case 'presence':
      return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
    default:
      return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
  }
}

function getEventTypeColor(type) {
  return type === 'sent' ? 'bg-primary-500' : 'bg-green-500';
}

// Слушаем сообщения от PushX
pushx.on('message', (msg) => {
  addEvent({
    type: 'received',
    channel: msg.channel,
    event: msg.event,
    data: msg.data,
    timestamp: formatTime(new Date()),
  });
});

pushx.on('connected', () => {
  success('Подключено', `Socket ID: ${pushx.socketId.value}`);
});

pushx.on('auth_error', (data) => {
  const isTimeout = data.error && (data.error.includes('timeout') || data.error.includes('Authentication timeout'));
  if (isTimeout) {
    showError('Соединение разорвано', 'Не было подписок на канал. Подключитесь снова и подпишитесь на канал в течение ~10 секунд.');
  } else {
    showError('Ошибка авторизации', data.error);
  }
});

// Lifecycle
onMounted(() => {
  fetchApplications();
  fetchBillingInfo();
});

onBeforeUnmount(() => {
  pushx.disconnect();
});
</script>

<style scoped>
.events-container {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #1f2937;
}

.events-container::-webkit-scrollbar {
  width: 6px;
}

.events-container::-webkit-scrollbar-track {
  background: #1f2937;
}

.events-container::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 3px;
}

.events-container::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
</style>
